import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type {
  ModelProviderSecret,
  PersistableModelProvider,
  ResourceOwnerInput,
} from '@reflex/shared';
import {
  ModelProviderSecretNameSchema,
  defaultOwnerForUser,
  ownerKindOf,
  providerSupportsBaseUrl,
} from '@reflex/shared';
import { useAuth, useMyTeams, useCreateModelProviderSecret } from '@reflex/ui/client/hooks';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SecretInput } from '../forms/SecretInput';
import { Label } from '../ui/label';
import { OwnerSelect, type OwnerSelectTeamOption } from '../ownership/OwnerSelect';

/** True when `raw` parses as an absolute URL. Mirrors `z.string().url()`. */
function isValidUrl(raw: string): boolean {
  try {
    new URL(raw);
    return true;
  } catch {
    return false;
  }
}

export interface AddModelProviderKeyFormProps {
  /** Provider the new key is for — fixed by the surface that renders the form. */
  provider: PersistableModelProvider;
  /** Fired with the created row so the opener can select it immediately. */
  onCreated?: (secret: ModelProviderSecret) => void;
  /** Fired when the user dismisses the form (Cancel button). */
  onCancel: () => void;
  /** Optional class applied to the form root. Use to tighten spacing in compact surfaces. */
  className?: string;
}

/**
 * Compact "add a model-provider key" form body. Shared between the
 * {@link AddModelProviderKeyDialog} wrapper and the inline create-mode of
 * the {@link ModelProviderSecretPicker} popover so the two surfaces stay
 * pixel-identical and only one set of validation / mutation wiring exists.
 *
 * Provider is fixed by the caller. The owner picker lets the user save
 * the key to themselves or one of their teams; org-tier keys go through
 * the dedicated settings page so their gateway-sync server hooks fire,
 * so org is intentionally not offered here.
 */
export function AddModelProviderKeyForm({
  provider,
  onCreated,
  onCancel,
  className,
}: AddModelProviderKeyFormProps) {
  const { user } = useAuth();
  const { data: teamsData } = useMyTeams();
  const create = useCreateModelProviderSecret();

  const userId = user?.id ?? '';
  const [owner, setOwner] = useState<ResourceOwnerInput>(() => defaultOwnerForUser(userId));
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const teams: OwnerSelectTeamOption[] = (teamsData ?? []).map((t) => {
    const team = t as { id?: string; name?: string };
    return { ownerTeamId: team.id ?? '', name: team.name ?? '' };
  });

  const trimmedName = name.trim();
  const nameResult = ModelProviderSecretNameSchema.safeParse(trimmedName);
  const nameError =
    trimmedName.length > 0 && !nameResult.success ? nameResult.error.issues[0]?.message : undefined;

  const ownerKind = ownerKindOf(owner.ownerId);
  const ownerReady = ownerKind === 'user' || ownerKind === 'team';
  const baseUrlSupported = providerSupportsBaseUrl(provider);
  const trimmedBaseUrl = baseUrl.trim();
  const baseUrlError =
    baseUrlSupported && trimmedBaseUrl.length > 0 && !isValidUrl(trimmedBaseUrl)
      ? 'Enter a valid URL, e.g. https://gateway.example.com'
      : undefined;
  const canSubmit =
    nameResult.success && Boolean(value.trim()) && ownerReady && !baseUrlError && !create.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!nameResult.success || baseUrlError) return;
    const trimmedValue = value.trim();
    if (!trimmedValue || !ownerReady) return;
    create.mutate(
      {
        scope: ownerKind === 'team' ? 'team' : 'user',
        teamId: ownerKind === 'team' ? (owner.ownerId ?? undefined) : undefined,
        provider,
        name: nameResult.data,
        value: trimmedValue,
        baseUrl: baseUrlSupported && trimmedBaseUrl.length > 0 ? trimmedBaseUrl : undefined,
      },
      {
        onSuccess: (secret) => {
          onCreated?.(secret);
        },
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} className={className} data-testid="add-model-provider-key-form">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="add-mps-name">Name</Label>
          <Input
            id="add-mps-name"
            placeholder="e.g. personal, billing, team-shared"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            autoFocus
            aria-invalid={nameError !== undefined}
          />
          {nameError ? <p className="text-xs text-destructive">{nameError}</p> : null}
        </div>

        <OwnerSelect
          currentUserId={userId}
          teams={teams}
          allowOrganization={false}
          value={owner}
          onChange={setOwner}
          label="Save to"
          description="Personal keys are only yours; team keys are shared with that team."
        />

        <div className="space-y-2">
          <Label htmlFor="add-mps-value">API key</Label>
          <SecretInput
            id="add-mps-value"
            placeholder="Paste the provider API key…"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>

        {baseUrlSupported && (
          <div className="space-y-2" data-testid="add-mps-advanced-section">
            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              aria-expanded={showAdvanced}
              aria-controls="add-mps-base-url"
            >
              {showAdvanced ? (
                <ChevronDown className="size-3" />
              ) : (
                <ChevronRight className="size-3" />
              )}
              Advanced
            </button>
            {showAdvanced && (
              <div className="space-y-2">
                <Label htmlFor="add-mps-base-url">Base URL (optional)</Label>
                <Input
                  id="add-mps-base-url"
                  placeholder="https://api.anthropic.com"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  autoComplete="off"
                  aria-invalid={baseUrlError !== undefined}
                />
                {baseUrlError ? (
                  <p className="text-xs text-destructive">{baseUrlError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Route this key through a custom endpoint. Leave blank to use the default API.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {create.isError ? (
          <p className={cn('text-xs text-destructive')}>
            {create.error instanceof Error ? create.error.message : 'Failed to save the key.'}
          </p>
        ) : null}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {create.isPending ? 'Saving…' : 'Save key'}
        </Button>
      </div>
    </form>
  );
}
